import Flag from 'react-flags';
import React, { useState, useRef, useEffect } from 'react';

import { t } from 'i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import styles from './AboutCompany.module.css';
import userAvatarPlaceholder from '../../../../../../assets/images/other/user_avatar_placeholder.png';
import { getTimeAgo } from '../../../../../../utils/formatHelpers';
import {
    AddUserIcon,
    Briefcase,
    ClockBlue,
    Message,
    TopRightArrow,
    VerifyGreenBadge
} from '../../../../../../assets/images/icons';
import { createChatroom } from '../../../../../../services/chatService';

function stripHtmlTags(html) {
    const div = document.createElement('div');
    div.innerHTML = html || '';
    return div.textContent || div.innerText || '';
}

function AboutCompany({ job }) {
    const user = useSelector(state => state.user.user);

    let city = job.city;
    if (t('AboutCompany.general.city.' + city) !== 'AboutCompany.general.city.' + city) {
        city = t('AboutCompany.general.city.' + city);
    }

    const profileDataUI = (icon, title, desc, isCenter) => {
        return (
            <div className={isCenter ? `${styles.profileDataContainer} ${styles.paddingLeft}` : `${styles.profileDataContainer}`}>
                <div className={styles.featureContentContainer}>
                    <div className={styles.dataIconContainer}>
                        <img src={icon} alt={`profile data icon`} className={styles.profileDataIcon} />
                    </div>
                </div>
                <div className={styles.col}>
                    <p className={styles.profileDataTitle}>{title}</p>
                    {desc !== 'login_test' && <p className={styles.profileDataValue}>{desc}</p>}
                </div>
            </div>
        );
    };
    let is_online = false;
    const date_info = getTimeAgo(new Date(job?.employer?.last_visit));
    let last_seen = '';
    if (date_info.unit_plural !== 'month' && date_info.unit_plural !== 'months' && date_info.unit_plural !== 'year' && date_info.unit_plural !== 'years') {

        if (date_info.unit_plural === 'second' || date_info.unit_plural === 'seconds' || date_info.unit_plural === 'minute' || (date_info.unit_plural === 'minutes' && date_info.count < 5)) {
            last_seen = t('general.UI.online');
            is_online = true;
        }
        else {
            last_seen = date_info.count + ' ' + t('general.UI.' + date_info.unit_plural) + ' ' + t('general.UI.ago');
        }
    }

    const navigate = useNavigate();
    const [showFull, setShowFull] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const previewRef = useRef(null);

    const plainDescription = stripHtmlTags(job?.employer?.company_description);

    // Check if the preview is truncated (more than 5 lines)
    useEffect(() => {
        if (previewRef.current) {
            setIsTruncated(
                previewRef.current.scrollHeight > previewRef.current.clientHeight
            );
        }
    }, [plainDescription]);
     return (
        <div className={styles.container}>

            {job?.employer?.employer_status === 'individual' ?
                <h5 className={styles.jobTitle}>{t('About_the_employer')}</h5>
                :
                <h5 className={styles.jobTitle}>{t('AboutCompany.title')}</h5>
            }
            <div className={styles.companyWrapper}>
                <div className={styles.profileContainer}>
                    {job?.employer?.profile_image ? (
                        <img
                            src={process.env.REACT_APP_UPLOADS_PATH + job?.employer?.profile_image}
                            alt="company avatar"
                            className={styles.profilePicture}
                            onError={(e) => {
                                e.target.onerror = null; // Prevent infinite loop
                                e.target.src = userAvatarPlaceholder; // Set fallback image
                            }}
                        />
                    ) : (
                        <img src={userAvatarPlaceholder} alt="company avatar" className={styles.profilePicture} />
                    )}
                    {is_online ?
                        <div className={styles.onlineStatus}></div>
                        :
                        <div className={styles.offlineStatus} ></div>
                    }

                </div>
                <div className={styles.job_card_head_info_main}>
                    <h5
                        onClick={() => { window.location.href = `${'/employer'}s/${job?.employer?.slug}`; }}
                        style={{ cursor: "pointer", }}
                        className={styles.jobTitle}>
                        {job?.employer?.employer_status === 'individual' ?
                            job?.employer?.first_name + job?.employer?.last_name
                            :
                            <>
                                {job?.employer?.company_name}
                                {job?.employer?.subscriptionStatus == 'active' &&
                                    <img src={VerifyGreenBadge} alt="verified badge" />
                                }
                            </>
                        }
                    </h5>
                    <div>
                    {job?.employer?.country&& <Flag name={job?.employer?.country} format="svg" shiny={false} basePath="/vendor/flags" />}
                        <p className={styles.jobDescription} >{job?.employer?.country && t('general.country.' + job?.employer?.country)}{job?.employer?.city &&", "+ job?.employer?.city}</p>

                    </div>
                </div>
                <img src={AddUserIcon} className={styles.addIcon} alt="add user icon" />
            </div>
            {!showFull ? (
                <div style={{ position: 'relative' }}>
                    <div
                        className={styles.descriptionClamp}
                        ref={previewRef}
                        aria-label="Company description preview"
                    >
                        {plainDescription}
                    </div>
                    {isTruncated && (
                        <span
                            className={styles.readMore}
                            onClick={() => setShowFull(true)}
                            role="button"
                            tabIndex={0}
                        >
                            {t('general.UI.readMore') || 'Read more'}
                        </span>
                    )}
                </div>
            ) : (
                <div className={styles.description} dangerouslySetInnerHTML={{ __html: job?.employer?.company_description }}></div>
            )}

            <div className={styles.featureContainer}>
                {job?.employer?.company_industries?.length > 0 && profileDataUI(Briefcase, t('AboutCompany.industry'), t('general.company_industries.' + job.employer.company_industries.split(';')[0]))}
                {profileDataUI(ClockBlue, t('AboutCompany.lastActive'), last_seen)}
            </div>
            <div className={styles.buttonsWrapper}>
                <button onClick={() => {
                    navigate(`/employers/${job.employer.slug}`)
                }} className={styles.viewProfileButton}>
                    {t('AboutCompany.viewProfile')}
                    <img src={TopRightArrow} alt="arrow icon" className={styles.arrowIcon} />
                </button>
                {user?.verification_status == 'approved' &&
                    user?.account_type == 'jobseeker' &&
                    <button className={styles.messageButtonDark}
                        onClick={async () => {
                            if (job.employer_id && user.id) {
                                await createChatroom(job.employer_id, user.id);
                                navigate('/chat', {
                                    state: {
                                        employerId: job.employer_id,
                                        jobSeekerId: user.id,
                                    }
                                })
                            }
                        }}
                    >
                        <img src={Message} alt={t('AboutCompany.downloadCV')} />
                        {t('sidebar.send_message')}
                    </button>
                }
            </div>

        </div>
    );
}

export default AboutCompany;
